import React, { useEffect, useState } from 'react'
import DashboardLayout from '../../../../components/layouts/DashboardLayout'
import { Container, Dropdown } from 'react-bootstrap'
import PageHeading from '../../../../components/pageHeading'
import styles from './payoutTransaction.module.scss'
import { useLocation, useNavigate } from 'react-router-dom'
import InputField from '../../../../components/theme/InputField'
import { RxCross1 } from 'react-icons/rx'
import searchIcon from '../../../../assests/svg/searchIcon.svg'
import ReactSelectField from '../../../../components/theme/ReactSelectField'
import ReactTable from '../../../../components/reactTable/ReactTable'
import useFetch from '../../../../hooks/useFetch'
import usePaginatedOrgOptions from '../../../../hooks/usePaginatedOrgOptions'
import { notifyBugsnagError, removeDashed, shouldTriggerSearch, showToast } from '../../../../common/utils/Functions'
import moment from 'moment'
import RangeDateModal from '../../../../components/rangeDateModal/rangeDateModal'
import ReactExcel from '../../../../components/reactExcel/ReactExcel'

const PayoutTransactions = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const [searchValue, setSearchValue] = useState("");
    const [payoutTransactionData, setPayoutTransactionData] = useState<any>([]);
    const [excelData, setExcelData] = useState<any>([]);

    const {
        options: filterValues,
        loading: orgLoading,
        loadMore: loadMoreOrgs,
        handleInputChange: handleOrgSearch,
    } = usePaginatedOrgOptions();
    const [pageLimit, setPageLimit] = useState<number>(10);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [sortBy, setSortBy] = useState<1 | 2 | 3 | null>(null);
    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);
    const [showRange, setShowRange] = useState(false);
    const [organizationUuid, setOrganizationUuid] = useState(null);
    const [selectedFilter, setSelectedFilter] = useState<{
        value: string;
        label: string;
    } | null>(null);
    const handleShowRange = () => setShowRange(true);
    const handleCloseRange = () => setShowRange(false);



    /** Api definition */
    const [
        getPayoutTransactionData,
        {
            response: payoutTransactionResponse,
            error: payoutTransactionError,
            loading: payoutTransactionLoading,
        },
    ] = useFetch("/admin/payouts-transaction-list", {
        method: "POST",
    });

    const [
        getExcelData,
        {
            response: excelDataResponse,
            error: excelDataError,
            loading: excelDataLoading,
        },
    ] = useFetch("/admin/payouts-transaction-list", {
        method: "POST",
    });


    useEffect(() => {
        if (!shouldTriggerSearch(searchValue)) return;
        const timer = setTimeout(() => {
            try {
                getPayoutTransactionData({
                    start: (currentPage - 1) * pageLimit,
                    limit: pageLimit,
                    sortedBy: sortBy,
                    search: searchValue,
                    startDate,
                    endDate,
                    organization_uuid: organizationUuid

                });
            } catch (e: any) {
                notifyBugsnagError(e, { api: "getEscrowData" });
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [
        searchValue,
        currentPage,
        pageLimit,
        searchValue,
        sortBy,
        startDate,
        endDate,
        organizationUuid,
    ]);


    const handleDateFilter = (value: string) => {
        const today = moment().format("MM/DD/YYYY");
        let startDate = null;
        let endDate = null;
        switch (value) {
            case "week":
                startDate = moment().startOf("week").format("MM/DD/YYYY");
                break;
            case "month":
                startDate = moment().startOf("month").format("MM/DD/YYYY");
                break;
            case "today":
                startDate = today;
                break;
            case "all":
                break;
            default:
                break;
        }
        if (value !== "all" && startDate) {
            endDate = today;
        }
        setStartDate(startDate);
        setEndDate(endDate);
    };

    useEffect(() => {
        if (location.state?.hasOwnProperty("filterValue")) {
            setSelectedFilter(location.state.filterValue);
            handleDateFilter(location.state.filterValue.value);
        }
    }, [location]);

    const handleExcelApi = () => {
        try {
            getExcelData({
                start: 0,
                limit: -1,
                startDate,
                endDate,
                organization_uuid: organizationUuid,
                sortedBy: sortBy,
                search: searchValue,
            });
        } catch (e: any) {
            notifyBugsnagError(excelDataError, {
                api: "getExcelData",
            });
        }
    };



    const filters = [
        { value: "all", label: "All" },
        { value: "today", label: "Today" },
        { value: "week", label: "This Week" },
        { value: "month", label: "This Month" },
        { value: "Custom", label: "Custom" },
    ];

    const options = [
        { value: 1, label: "Date" },
        { value: 2, label: "Highest Tip Amount" },
        { value: 3, label: "Lowest Tip Amount" },
    ];

    const headings =
        [
            {
                label: "Organization Name",
                key: "organizationName",
            },
            { label: "Payout Type", key: "payout_type" },
            { label: "Gross Payout Amount", key: "gross_payment_amount" },
            { label: "Net Payout Amount", key: "net_payout_amount" },
            { label: "Date", key: "date" },

        ]

    useEffect(() => {
        if (payoutTransactionResponse) {
            setTotalRecords(payoutTransactionResponse.data?.totalRecords);
            let updatedList;
            updatedList = payoutTransactionResponse.data?.list?.map((item: any) => ({
                organizationName: item?.org_name ? item?.org_name : removeDashed,
                payout_type: item?.is_manual_transfer === 1 ? "Manual" : "Stripe",
                gross_payment_amount: item?.total_amount ? item?.total_amount?.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                }) : removeDashed,
                net_payout_amount: item?.net_tip_amount ? item?.net_tip_amount?.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                }) : removeDashed,
                date: moment(item?.createdAt).format("MM/DD/YYYY") ?? removeDashed,
            }))

            setPayoutTransactionData(updatedList);
        } if (payoutTransactionError) {
            showToast(payoutTransactionError.message as string, "error");
        }
    }, [payoutTransactionResponse, payoutTransactionError])

    const excelHeaders =
        [
            { key: "org_name", header: "Organization Name	", width: 40 },
            { key: "payout_type", header: "Payout Type", width: 40 },
            { key: "gross_payment_amount", header: "Gross Payout Amount", width: 40 },
            { key: "net_payout_amount", header: "Net Payout Amount", width: 40 },
            { key: "date", header: "Date", width: 40 },

        ]

    useEffect(() => {
        if (excelDataResponse) {
            let updateOrgExcel;
            updateOrgExcel = excelDataResponse.data.list?.map((item: any) => {
                return {
                    org_name: item?.org_name ?? removeDashed,
                    payout_type: item?.is_manual_transfer === 1 ? "Manual" : "Stripe",
                    gross_payment_amount: item?.total_amount ? item?.total_amount?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                    }) : removeDashed,
                    net_payout_amount: item?.net_tip_amount ? item?.net_tip_amount?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                    }) : removeDashed,
                    date: moment(item?.createdAt).format("MM/DD/YYYY") ?? removeDashed,

                };
            });

            setExcelData([...updateOrgExcel]);
        }
        if (excelDataError) {
            showToast(excelDataError.message as string, "error");
        }
    }, [excelDataResponse, excelDataError]);



    const DropdownItems = () => {
        return (
            <div
                style={{
                    border: "1px solid #A9B0BF",
                    paddingLeft: "11px",
                    width: "34px",
                    borderRadius: "4px",
                    backgroundColor: "#ededed",
                }}
            >
                {" "}
                <Dropdown
                    align="end"
                    className={styles.dropDots}
                >
                    <Dropdown.Toggle variant="light" id="dropdown-basic" style={{ backgroundColor: 'transparent', border: 'none', transform: 'rotate(90deg)', color: '#676d7a', fontSize: '20px' }}>
                        ...
                    </Dropdown.Toggle>

                    <Dropdown.Menu
                        style={{
                            maxWidth: "max-content",
                            minWidth: "150px",
                            zIndex: "99",
                        }}
                    >
                        <Dropdown.Item
                            onClick={() => {
                                navigate('/reporting')
                            }}
                        >
                            {" "}
                            Tip Transactions
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={() => {
                                navigate('/reporting/conversion-tracking')
                            }}
                        >
                            {" "}
                            Conversion Tracking
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={() => {
                                navigate('/reporting/escrow-balance')
                            }}
                        >
                            {" "}
                            Escrow Balances
                        </Dropdown.Item>

                    </Dropdown.Menu>
                </Dropdown>
            </div>
        );
    };
    return (
        <DashboardLayout>
            <div className={styles.dash_page}>
                <Container>
                    <PageHeading
                        heading={

                            <span style={{ display: 'flex', gap: '10px' }}>
                                Payout Transactions
                                <DropdownItems />
                            </span>
                        }
                        subHeading="Here is the information about all your reports."
                    >
                        <div className={styles.search}>
                            <div className={styles.searchField}>
                                <InputField
                                    name="search_organization_name"
                                    icon={searchIcon}
                                    placeholder="Search by Organization Name"
                                    value={searchValue}
                                    onChange={(e) => {
                                        setCurrentPage(1);
                                        setSearchValue(e.target.value);
                                    }}
                                    autoComplete="off"
                                />
                                {searchValue ? (
                                    <span
                                        className={styles.crossIcon}
                                        onClick={() => setSearchValue("")}
                                    >
                                        <RxCross1 />
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    </PageHeading>
                    <div className={styles.tipsWrap}>
                        <ReactSelectField
                            name="sortBy"
                            options={options}
                            label="Sort By"
                            onChange={(e: any) => {
                                setCurrentPage(1);
                                setSortBy(e.value);
                            }}
                        />
                        <ReactSelectField
                            name="Sort By"
                            options={filters}
                            label="Filter By"
                            value={selectedFilter}
                            onChange={(e: any) => {
                                if (e.value === "Custom") {
                                    handleShowRange();
                                }
                                handleDateFilter(e.value);
                                setSelectedFilter(e);
                            }}
                        />
                        <ReactSelectField
                            name={"organizations"}
                            options={filterValues}
                            label={"Organizations"}
                            onLoadMore={loadMoreOrgs}
                            isLoadingMore={orgLoading}
                            onInputChange={handleOrgSearch}
                            onChange={(e: any) => {
                                setOrganizationUuid(e.value);
                            }}
                        />


                        <ReactExcel
                            setExcelData={setExcelData}
                            loading={excelDataLoading}
                            onClick={handleExcelApi}
                            buttonTitle="Export CSV"
                            fileType="xlsx"
                            fileName="Payout Transactions List"
                            coulmns={excelHeaders}
                            excelData={excelData}
                        />

                    </div>
                    <ReactTable
                        data={payoutTransactionData || []}
                        headings={headings}
                        actionColumn={false}
                        currentPage={currentPage}
                        itemPerPage={pageLimit}
                        totalItems={totalRecords}
                        setPerPageLimit={setPageLimit}
                        pageLimit={pageLimit}
                        setCurrentPage={setCurrentPage}
                        pagination={true}
                        statusColumn={false}
                        loading={payoutTransactionLoading}
                    />
                    {selectedFilter?.value === "Custom" && (
                        <RangeDateModal
                            show={showRange}
                            handleClose={handleCloseRange}
                            setEndDate={setEndDate}
                            setStartDate={setStartDate}
                        />
                    )}
                </Container>
            </div>
        </DashboardLayout>
    )
}

export default PayoutTransactions